/**
 * ERP stock check for Design Outlet items. Overview, request/response and
 * modes: snippets/stock-check.liquid.
 */
(function () {
  'use strict';

  var configEl = document.querySelector('[data-stock-check-config]');
  var dialog = document.querySelector('[data-stock-check-dialog]');
  if (!configEl || !dialog) return;

  var config = JSON.parse(configEl.textContent);
  var text = config.text;

  // Test mode never runs on the published theme.
  var publishedTheme = window.Shopify && Shopify.theme && Shopify.theme.role === 'main';
  if (config.mode === 'test' && publishedTheme) return;
  if (config.mode === 'live' && !config.endpoint) return;
  if (config.mode !== 'test' && config.mode !== 'live') return;

  var REQUEST_TIMEOUT = 10000;
  // How long a product page result is trusted before the next click re-checks.
  var RESULT_TTL = 60000;

  function log(level, message, data) {
    if (window.console && console[level]) console[level]('[stock-check] ' + message, data === undefined ? '' : data);
  }

  // ===========================================================================
  // ERP
  // ===========================================================================

  // items: [{ variant_id, sku, vendor_id, quantity }]
  function checkStock(items) {
    var payload = { items: items };
    if (config.mode === 'test') return mockStock(payload);

    var controller = window.AbortController ? new AbortController() : null;
    var timer = controller && setTimeout(function () { controller.abort(); }, REQUEST_TIMEOUT);
    return fetch(config.endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
      signal: controller ? controller.signal : undefined
    })
      .then(function (response) {
        clearTimeout(timer);
        if (!response.ok) throw new Error('Stock check failed: ' + response.status);
        return response.json();
      }, function (error) {
        clearTimeout(timer);
        throw error;
      });
  }

  // Test response: quantity 1 -> 0 available, 2+ -> 1 available.
  // ?stock_check=ok answers "all available", ?stock_check=error fails.
  function mockStock(payload) {
    var scenario = new URLSearchParams(window.location.search).get('stock_check');
    log('info', 'test mode request:', payload);
    return new Promise(function (resolve, reject) {
      setTimeout(function () {
        if (scenario === 'error') {
          reject(new Error('Test mode: simulated ERP failure'));
          return;
        }
        var response = {
          items: payload.items.map(function (item) {
            var available = item.quantity > 1 ? 1 : 0;
            if (scenario === 'ok') available = item.quantity;
            return { variant_id: item.variant_id, sku: item.sku, available: available };
          })
        };
        log('info', 'test mode response:', response);
        resolve(response);
      }, 800);
    });
  }

  // What the ERP says is available for an item, or null when it didn't say.
  function availableFor(item, response) {
    var results = (response && response.items) || [];
    for (var i = 0; i < results.length; i++) {
      var result = results[i];
      var matches = result.variant_id != null
        ? String(result.variant_id) === String(item.variant_id)
        : result.sku && result.sku === item.sku;
      if (!matches) continue;
      var available = Math.floor(Number(result.available));
      return isNaN(available) ? null : Math.max(0, available);
    }
    return null;
  }

  // ===========================================================================
  // Dialog
  // ===========================================================================

  var ui = {
    title: dialog.querySelector('[data-stock-check-title]'),
    lead: dialog.querySelector('[data-stock-check-lead]'),
    list: dialog.querySelector('[data-stock-check-list]'),
    note: dialog.querySelector('[data-stock-check-note]'),
    error: dialog.querySelector('[data-stock-check-error]'),
    confirm: dialog.querySelector('[data-stock-check-confirm]'),
    dismiss: dialog.querySelector('[data-stock-check-dismiss]'),
    cancels: dialog.querySelectorAll('[data-stock-check-cancel]'),
    status: document.querySelector('[data-stock-check-status]')
  };
  var active = null; // options passed to openDialog
  var dialogBusy = false;

  /**
   * options:
   *   title, lead, note, removedText
   *   items: [{ title, variant_title, sku, image, requested, available,
   *             badge?, hideQuantity? }]
   *     available 0 shows the item as removed, unless badge is set.
   *     badge replaces the "Only N available" text; hideQuantity drops the
   *     "Quantity X -> Y" line.
   *   confirmLabel: omit for a dialog with only the dismiss button
   *   dismissLabel
   *   outOfStock: red icon
   *   onConfirm(): Promise; the dialog stays busy until it settles, and
   *     shows text.updateError if it rejects
   *   returnFocus: element to focus after closing
   */
  function openDialog(options) {
    active = options;
    dialog.classList.toggle('stock-check--out', !!options.outOfStock);
    ui.title.textContent = options.title;
    ui.lead.textContent = options.lead;
    ui.list.innerHTML = '';
    options.items.forEach(function (item) {
      ui.list.appendChild(renderItem(item, options.removedText));
    });
    setTextWithEmailLinks(ui.note, options.note || '');
    ui.error.hidden = true;
    ui.dismiss.textContent = options.dismissLabel;
    ui.confirm.hidden = !options.confirmLabel;
    ui.confirm.textContent = options.confirmLabel || '';
    ui.dismiss.classList.toggle('stock-check__button--primary', !options.confirmLabel);
    ui.dismiss.classList.toggle('stock-check__button--secondary', !!options.confirmLabel);
    setDialogBusy(false);

    document.documentElement.style.overflow = 'hidden';
    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');
    (options.confirmLabel ? ui.confirm : ui.dismiss).focus();
  }

  function closeDialog() {
    if (!active) return;
    var returnFocus = active.returnFocus;
    active = null;
    setDialogBusy(false);
    if (typeof dialog.close === 'function' && dialog.open) dialog.close();
    else dialog.removeAttribute('open');
    document.documentElement.style.overflow = '';
    if (returnFocus && returnFocus.isConnected) returnFocus.focus();
  }

  function setDialogBusy(on) {
    dialogBusy = on;
    ui.confirm.disabled = on;
    ui.confirm.classList.toggle('is-busy', on);
    ui.confirm.setAttribute('aria-busy', on ? 'true' : 'false');
    Array.prototype.forEach.call(ui.cancels, function (button) { button.disabled = on; });
    announce(on ? text.updating : '');
  }

  function announce(message) {
    if (ui.status) ui.status.textContent = message;
  }

  function renderItem(item, removedText) {
    var removed = item.available === 0 && !item.badge;
    var li = el('li', 'stock-check__item' + (removed ? ' stock-check__item--removed' : ''));
    if (item.image) {
      var img = el('img', 'stock-check__thumb');
      img.src = item.image;
      img.alt = '';
      img.loading = 'lazy';
      li.appendChild(img);
    }

    var info = el('div', 'stock-check__info');
    info.appendChild(el('p', 'stock-check__name', item.title));

    var meta = [];
    if (item.variant_title) meta.push(item.variant_title);
    if (item.sku) meta.push(text.sku + ' ' + item.sku);
    if (meta.length) info.appendChild(el('p', 'stock-check__meta', meta.join(' · ')));

    var change = el('p', 'stock-check__change');
    if (removed) {
      change.appendChild(el('span', 'stock-check__badge stock-check__badge--removed', text.removedBadge));
      if (removedText) change.appendChild(el('span', '', removedText));
    } else {
      change.appendChild(el('span', 'stock-check__badge stock-check__badge--adjusted',
        item.badge || text.adjustedBadge.replace('[count]', item.available)));
      if (!item.hideQuantity) {
        var quantity = el('span', '', text.quantity + ' ');
        quantity.appendChild(el('span', 'stock-check__qty-from', String(item.requested)));
        quantity.appendChild(el('span', 'stock-check__qty-arrow', '→'));
        quantity.appendChild(el('span', 'stock-check__qty-to', String(item.available)));
        change.appendChild(quantity);
      }
    }
    info.appendChild(change);
    li.appendChild(info);
    return li;
  }

  // Plain text, with any email address turned into a mailto link.
  function setTextWithEmailLinks(node, value) {
    node.textContent = '';
    value.split(/([\w.+-]+@[\w-]+(?:\.[\w-]+)+)/).forEach(function (part, index) {
      if (index % 2 === 0) {
        if (part) node.appendChild(document.createTextNode(part));
        return;
      }
      var link = el('a', '', part);
      link.href = 'mailto:' + part;
      node.appendChild(link);
    });
  }

  function el(tag, className, content) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (content != null) node.textContent = content;
    return node;
  }

  function dismissDialog() {
    if (dialogBusy) return;
    closeDialog();
  }

  Array.prototype.forEach.call(ui.cancels, function (button) {
    button.addEventListener('click', dismissDialog);
  });
  // Esc
  dialog.addEventListener('cancel', function (event) {
    event.preventDefault();
    dismissDialog();
  });
  // Click on the backdrop
  dialog.addEventListener('click', function (event) {
    if (event.target === dialog) dismissDialog();
  });

  ui.confirm.addEventListener('click', function () {
    if (!active || dialogBusy || !active.onConfirm) return;
    setDialogBusy(true);
    ui.error.hidden = true;
    active.onConfirm().catch(function (error) {
      log('error', '', error);
      setDialogBusy(false);
      ui.error.textContent = text.updateError;
      ui.error.hidden = false;
    });
  });

  // ===========================================================================
  // Checkout from the cart page or the add-to-cart popup
  // ===========================================================================

  var cartCheckRunning = false;
  var passThrough = false;

  // Capture on document runs before the theme's own submit handlers, so
  // nothing else sees the submit until the check has passed.
  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (passThrough || !(form instanceof HTMLFormElement)) return;

    // Add to cart on a Design Outlet product (see the product section below).
    if (productStates && productData(form)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      runAddToCartCheck(form, event.submitter || form.querySelector('[data-product-atc]'));
      return;
    }

    var submitter = event.submitter || form.querySelector('button[name="checkout"]');
    if (!submitter || submitter.name !== 'checkout') return;

    event.preventDefault();
    event.stopImmediatePropagation();
    if (cartCheckRunning) return;
    runCartCheck(form, submitter);
  }, true);

  function runCartCheck(form, submitter) {
    cartCheckRunning = true;
    setChecking(submitter, true);

    fetchCartLines()
      .then(function (data) {
        if (!data.items.length) return null;
        return checkStock(cartPayload(data.items)).then(function (response) {
          return cartShortages(data, response);
        });
      })
      .then(function (result) {
        cartCheckRunning = false;
        setChecking(submitter, false);
        if (!result || !result.shortages.length) {
          proceedToCheckout(form, submitter, false);
          return;
        }
        showCartDialog(form, submitter, result);
      })
      .catch(function (error) {
        // Fail open: an unreachable ERP must not block sales.
        log('warn', 'skipped:', error);
        cartCheckRunning = false;
        setChecking(submitter, false);
        proceedToCheckout(form, submitter, false);
      });
  }

  function setChecking(button, on) {
    button.classList.toggle('is-stock-checking', on);
    button.setAttribute('aria-busy', on ? 'true' : 'false');
    announce(on ? text.checking : '');
  }

  function fetchCartLines() {
    return fetch(config.linesUrl, { credentials: 'same-origin', cache: 'no-store' })
      .then(function (response) {
        if (!response.ok) throw new Error('Cart lines request failed: ' + response.status);
        return response.text();
      })
      .then(function (body) { return JSON.parse(body); });
  }

  // One entry per variant; a variant can sit on several lines when the lines
  // carry different properties.
  function cartPayload(lines) {
    var byVariant = {};
    var items = [];
    lines.forEach(function (line) {
      var item = byVariant[line.variant_id];
      if (!item) {
        item = byVariant[line.variant_id] = {
          variant_id: line.variant_id,
          sku: line.sku,
          vendor_id: line.vendor_id,
          quantity: 0
        };
        items.push(item);
      }
      item.quantity += line.quantity;
    });
    return items;
  }

  function cartShortages(data, response) {
    var shortages = [];
    var removedUnits = 0;

    cartPayload(data.items).forEach(function (item) {
      var available = availableFor(item, response);
      if (available === null || available >= item.quantity) return;

      // Spread what is available over the variant's lines, first line first.
      var remaining = available;
      var lines = data.items.filter(function (line) { return line.variant_id === item.variant_id; });
      var updates = lines.map(function (line) {
        var quantity = Math.min(line.quantity, remaining);
        remaining -= quantity;
        return { key: line.key, quantity: quantity };
      });

      removedUnits += item.quantity - available;
      shortages.push({
        item: {
          title: lines[0].title,
          variant_title: lines[0].variant_title,
          sku: lines[0].sku,
          image: lines[0].image,
          requested: item.quantity,
          available: available
        },
        updates: updates
      });
    });

    return {
      shortages: shortages,
      itemCount: data.item_count,
      emptiesCart: data.item_count - removedUnits <= 0
    };
  }

  function showCartDialog(form, submitter, result) {
    // The add-to-cart popup traps focus and removes itself when it closes;
    // close it now so the dialog has the page to itself.
    var popup = form.closest('[data-atc-banner]');
    if (popup) {
      var closeButton = popup.querySelector('[data-atc-banner-close]');
      if (closeButton) closeButton.click();
    }

    var affectedUnits = result.shortages.reduce(function (sum, s) { return sum + s.item.requested; }, 0);
    var note = '';
    if (result.emptiesCart) note = text.empty;
    else if (affectedUnits < result.itemCount) note = text.others;

    openDialog({
      title: text.cartTitle,
      lead: text.cartLead,
      note: note,
      removedText: text.removedText,
      items: result.shortages.map(function (s) { return s.item; }),
      confirmLabel: result.emptiesCart ? text.confirmEmpty : text.confirm,
      dismissLabel: popup ? text.continueShopping : text.backToCart,
      returnFocus: popup ? null : submitter,
      onConfirm: function () {
        var updates = {};
        result.shortages.forEach(function (s) {
          s.updates.forEach(function (update) { updates[update.key] = update.quantity; });
        });
        return fetch(config.updateUrl, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates: updates })
        })
          .then(function (response) {
            if (!response.ok) throw new Error('Cart update failed: ' + response.status);
            return response.json();
          })
          .then(function (cart) {
            // Stay busy: the page is about to change.
            if (!cart.item_count) window.location.reload();
            else proceedToCheckout(form, submitter, true);
          });
      }
    });
  }

  // Submit through the normal path so the theme and apps still see it.
  function proceedToCheckout(form, submitter, cartChanged) {
    if (!form.isConnected) {
      // The add-to-cart popup's form is gone; post a fresh one.
      var fresh = document.createElement('form');
      fresh.method = 'post';
      fresh.action = config.cartUrl;
      fresh.hidden = true;
      var input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'checkout';
      input.value = '1';
      fresh.appendChild(input);
      document.body.appendChild(fresh);
      HTMLFormElement.prototype.submit.call(fresh);
      return;
    }

    if (cartChanged) {
      // The cart page's quantity fields are out of date now; leave them out
      // so they don't overwrite what was just saved.
      Array.prototype.forEach.call(form.querySelectorAll('[name="updates[]"]'), function (field) {
        field.disabled = true;
      });
    }

    passThrough = true;
    submitter.disabled = false;
    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit(submitter);
    } else {
      var checkoutInput = document.createElement('input');
      checkoutInput.type = 'hidden';
      checkoutInput.name = 'checkout';
      checkoutInput.value = '1';
      form.appendChild(checkoutInput);
      form.submit();
    }
    // If something cancels the submit, the next click checks again.
    setTimeout(function () { passThrough = false; }, 0);
  }

  // ===========================================================================
  // Buy with Shop / More payment options on Design Outlet product pages
  //
  // Shopify's button can't be clicked from code, so a click that was stopped
  // can't be resumed. The selected variant and quantity are checked ahead of
  // time instead: when the buttons come into view, when the selection
  // changes, and again on hover once a result is over a minute old. A click
  // with an in-stock result goes through untouched; any other click is stopped.
  // ===========================================================================

  var PAYMENT_BUTTONS = '.shopify-payment-button';
  var CACHE_PREFIX = 'stock-check:';
  var productStates = typeof WeakMap === 'function' ? new WeakMap() : null;
  if (!productStates) return;

  function productData(form) {
    var state = productStates.get(form);
    if (state && state.data !== undefined) return state.data;
    var script = form.querySelector('[data-stock-check-product]');
    var data = null;
    if (script) {
      try { data = JSON.parse(script.textContent); } catch (error) { log('warn', 'bad product data', error); }
    }
    productStates.set(form, { data: data, result: null });
    return data;
  }

  function stateFor(form) {
    productData(form);
    return productStates.get(form);
  }

  function selection(form) {
    var data = productData(form);
    if (!data) return null;
    var idField = form.querySelector('[name="id"]');
    var variant = idField && data.variants[idField.value];
    if (!variant) return null;
    var quantityField = form.querySelector('input[name="quantity"]');
    var quantity = Math.max(1, parseInt(quantityField && quantityField.value, 10) || 1);
    return {
      key: idField.value + ':' + quantity,
      variant_id: Number(idField.value),
      sku: variant.sku,
      vendor_id: variant.vendor_id,
      quantity: quantity,
      title: data.title,
      variant_title: variant.variant_title,
      image: variant.image
    };
  }

  function fresh(result) {
    return result && !result.pending && Date.now() - result.at < RESULT_TTL;
  }

  function inStock(result, current) {
    return result.failed || result.available === null || result.available >= current.quantity;
  }

  function readCache(key) {
    try {
      var cached = JSON.parse(window.sessionStorage.getItem(CACHE_PREFIX + key));
      if (cached && Date.now() - cached.at < RESULT_TTL) {
        return { key: key, available: cached.available, failed: false, at: cached.at };
      }
    } catch (error) { /* storage unavailable */ }
    return null;
  }

  function writeCache(result) {
    try {
      window.sessionStorage.setItem(CACHE_PREFIX + result.key, JSON.stringify({ available: result.available, at: result.at }));
    } catch (error) { /* storage unavailable */ }
  }

  // Results by "variant:quantity", shared by every form on the page.
  var known = {};

  // Resolves with the result for a variant at a quantity, reusing a fresh or
  // in-flight one. The answer is capped at the quantity asked for, so it says
  // whether that many are available and, when fewer are, exactly how many.
  function lookup(current, quantity) {
    var key = current.variant_id + ':' + quantity;
    var result = known[key];
    if (result && result.pending) return result.pending;
    if (fresh(result)) return Promise.resolve(result);

    var cached = readCache(key);
    if (cached) {
      known[key] = cached;
      return Promise.resolve(cached);
    }

    result = known[key] = { key: key, available: null, failed: false, at: 0, pending: null };
    result.pending = checkStock([{
      variant_id: current.variant_id,
      sku: current.sku,
      vendor_id: current.vendor_id,
      quantity: quantity
    }])
      .then(function (response) {
        result.available = availableFor(current, response);
      }, function (error) {
        // Fail open: clicks go through for RESULT_TTL. Not saved to
        // sessionStorage, so a reload asks again.
        log('warn', 'product check failed:', error);
        result.failed = true;
      })
      .then(function () {
        result.at = Date.now();
        result.pending = null;
        if (!result.failed) writeCache(result);
        return result;
      });
    return result.pending;
  }

  function remember(variantId, quantity, available) {
    var result = { key: variantId + ':' + quantity, available: available, failed: false, at: Date.now(), pending: null };
    known[result.key] = result;
    writeCache(result);
    return result;
  }

  // Resolves with the result for the form's current selection, or null when
  // there is nothing to check.
  function precheck(form) {
    var current = selection(form);
    if (!current) return Promise.resolve(null);
    var state = stateFor(form);
    var pending = lookup(current, current.quantity);
    state.result = known[current.key];
    return pending.then(function (result) {
      state.result = result;
      return result;
    });
  }

  // ---- Add to cart ------------------------------------------------------
  // Checked against what the cart already holds: with 1 in the cart and 2 in
  // stock, adding 2 offers to add 1. The theme's own add to cart (and its
  // popup) runs once the check passes.

  var addToCartRunning = false;

  function runAddToCartCheck(form, button) {
    var current = selection(form);
    if (!current) {
      resumeAddToCart(form, button);
      return;
    }
    if (addToCartRunning) return;
    addToCartRunning = true;
    if (button) setChecking(button, true);

    var inCart = 0;
    quantityInCart(current.variant_id)
      .then(function (count) {
        inCart = count;
        return lookup(current, inCart + current.quantity);
      })
      .then(function (result) {
        addToCartRunning = false;
        if (button) setChecking(button, false);
        if (result.failed || result.available === null || result.available >= inCart + current.quantity) {
          resumeAddToCart(form, button);
          return;
        }
        showAddToCartDialog(form, button, current, inCart, result.available);
      })
      .catch(function (error) {
        log('warn', 'add to cart check skipped:', error);
        addToCartRunning = false;
        if (button) setChecking(button, false);
        resumeAddToCart(form, button);
      });
  }

  function quantityInCart(variantId) {
    return fetch(config.cartUrl + '.js', { credentials: 'same-origin', cache: 'no-store' })
      .then(function (response) {
        if (!response.ok) throw new Error('Cart request failed: ' + response.status);
        return response.json();
      })
      .then(function (cart) {
        return (cart.items || []).reduce(function (sum, line) {
          return line.variant_id === variantId ? sum + line.quantity : sum;
        }, 0);
      });
  }

  function showAddToCartDialog(form, button, current, inCart, available) {
    var canAdd = Math.max(0, available - inCart);
    var item = {
      title: current.title,
      variant_title: current.variant_title,
      sku: current.sku,
      image: current.image,
      requested: current.quantity,
      available: canAdd
    };
    var quantityField = form.querySelector('input[name="quantity"]');

    if (canAdd > 0) {
      if (inCart > 0) item.badge = text.moreBadge.replace('[count]', canAdd);
      openDialog({
        title: text.buyShortTitle,
        lead: inCart > 0
          ? text.atcMoreLead.replace('[inCart]', inCart).replace('[count]', canAdd)
          : text.buyShortLead,
        note: text.help,
        items: [item],
        confirmLabel: text.addQuantity.replace('[count]', canAdd),
        dismissLabel: text.cancel,
        returnFocus: quantityField,
        onConfirm: function () {
          // Record what is known, so the change below doesn't re-check.
          remember(current.variant_id, canAdd, canAdd);
          setQuantity(form, canAdd);
          closeDialog();
          resumeAddToCart(form, button);
          return Promise.resolve();
        }
      });
      return;
    }

    if (inCart > 0) {
      item.badge = text.inCartBadge.replace('[count]', inCart);
      item.hideQuantity = true;
      openDialog({
        title: text.atcFullTitle,
        lead: text.atcFullLead,
        note: text.help,
        items: [item],
        confirmLabel: text.viewCart,
        dismissLabel: text.close,
        returnFocus: quantityField,
        onConfirm: function () {
          window.location.href = config.cartUrl;
          return new Promise(function () {}); // stay busy while the page changes
        }
      });
      return;
    }

    openDialog({
      title: text.buyOutTitle,
      lead: text.buyOutLead,
      note: text.help,
      items: [item],
      outOfStock: true,
      dismissLabel: text.close,
      returnFocus: quantityField
    });
  }

  // Submit again through the normal path, so the theme adds to cart and
  // opens its popup as usual.
  function resumeAddToCart(form, button) {
    if (!form.isConnected) return;
    passThrough = true;
    try {
      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit(button && button.form === form ? button : undefined);
      } else if (button) {
        button.click();
      }
    } finally {
      passThrough = false;
    }
  }

  function whenIdle(callback) {
    if (window.requestIdleCallback) window.requestIdleCallback(callback, { timeout: 2000 });
    else setTimeout(callback, 200);
  }

  // Check once the buttons are on screen, not on every page view.
  function watch(form) {
    if (!productData(form)) return;
    var buttons = form.querySelector(PAYMENT_BUTTONS);
    if (!buttons) return;
    var state = stateFor(form);
    if (state.watched) return;
    state.watched = true;

    if (!window.IntersectionObserver) {
      state.visible = true;
      whenIdle(function () { precheck(form); });
      return;
    }
    var observer = new IntersectionObserver(function (entries) {
      if (!entries.some(function (entry) { return entry.isIntersecting; })) return;
      observer.disconnect();
      state.visible = true;
      whenIdle(function () { precheck(form); });
    });
    observer.observe(buttons);
  }

  function productForms() {
    return Array.prototype.map.call(document.querySelectorAll('[data-stock-check-product]'), function (script) {
      return script.closest('form');
    }).filter(Boolean);
  }

  productForms().forEach(watch);

  // Variant or quantity changed: check the new selection once the shopper
  // stops changing it. Only for buttons that have been on screen.
  var changeTimer = null;
  function onSelectionChange(event) {
    var form = event.target.closest && event.target.closest('form');
    if (!form || !productData(form)) return;
    clearHint(form);
    if (!stateFor(form).visible) return;
    clearTimeout(changeTimer);
    changeTimer = setTimeout(function () { precheck(form); }, 400);
  }
  document.addEventListener('change', onSelectionChange, true);
  document.addEventListener('input', onSelectionChange, true);

  // Hover or focus: refresh an old result before the click, and pick up
  // forms that appeared later (quick shop).
  function onApproach(event) {
    var buttons = event.target.closest && event.target.closest(PAYMENT_BUTTONS);
    var form = buttons && buttons.closest('form');
    if (!form || !productData(form)) return;
    var state = stateFor(form);
    state.visible = true;
    precheck(form);
  }
  document.addEventListener('pointerover', onApproach, true);
  document.addEventListener('focusin', onApproach, true);

  document.addEventListener('click', function (event) {
    var buttons = event.target.closest && event.target.closest(PAYMENT_BUTTONS);
    var form = buttons && buttons.closest('form');
    if (!form || !productData(form)) return;
    var current = selection(form);
    if (!current) return;

    var result = stateFor(form).result;
    var ready = result && result.key === current.key && fresh(result);
    if (ready && inStock(result, current)) return; // Let Shopify take it from here.

    event.preventDefault();
    event.stopImmediatePropagation();
    event.stopPropagation();
    clearHint(form);

    if (ready) {
      showFastBuyDialog(form, buttons, current, result);
      return;
    }

    buttons.classList.add('is-stock-checking');
    announce(text.checking);
    precheck(form).then(function (checked) {
      buttons.classList.remove('is-stock-checking');
      announce('');
      if (!checked) return;
      var now = selection(form);
      if (!now || now.key !== checked.key) return;
      if (inStock(checked, now)) showHint(form, buttons, text.hintReady);
      else showFastBuyDialog(form, buttons, now, checked);
    });
  }, true);

  function showFastBuyDialog(form, buttons, current, result) {
    var available = result.available;
    var item = {
      title: current.title,
      variant_title: current.variant_title,
      sku: current.sku,
      image: current.image,
      requested: current.quantity,
      available: available
    };
    var quantityField = form.querySelector('input[name="quantity"]');

    if (available === 0) {
      openDialog({
        title: text.buyOutTitle,
        lead: text.buyOutLead,
        note: text.help,
        items: [item],
        outOfStock: true,
        dismissLabel: text.close,
        returnFocus: quantityField
      });
      return;
    }

    openDialog({
      title: text.buyShortTitle,
      lead: text.buyShortLead,
      note: text.help,
      items: [item],
      confirmLabel: text.setQuantity.replace('[count]', available),
      dismissLabel: text.cancel,
      returnFocus: quantityField,
      onConfirm: function () {
        // Record the result first, so the change below doesn't re-check.
        stateFor(form).result = remember(current.variant_id, available, available);
        setQuantity(form, available);
        closeDialog();
        showHint(form, buttons, text.hintQuantity.replace('[count]', available));
        return Promise.resolve();
      }
    });
  }

  // The theme shows a 1-9 select and a text field for 10+; keep both in step.
  function setQuantity(form, quantity) {
    var select = form.querySelector('[data-quantity-select]');
    var input = form.querySelector('input[name="quantity"]');
    if (select && quantity <= 9) {
      select.value = String(quantity);
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }
    if (input) {
      input.value = String(quantity);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  function showHint(form, buttons, message) {
    clearHint(form);
    var hint = el('p', 'stock-check-hint', message);
    hint.setAttribute('role', 'status');
    hint.setAttribute('data-stock-check-hint', '');
    buttons.parentNode.insertBefore(hint, buttons.nextSibling);
  }

  function clearHint(form) {
    var hint = form.querySelector('[data-stock-check-hint]');
    if (hint) hint.parentNode.removeChild(hint);
  }
})();
